<template>
  <div class="max-w-4xl mx-auto bg-white shadow-lg" id="invoice">
    <!-- Invoice Header -->
    <div class="px-8 py-6 border-b border-gray-200">
      <div class="flex justify-between items-start">
        <div>
          <div class="flex items-center mb-4">
            <div class="w-10 h-10 bg-gradient-primary rounded-lg flex items-center justify-center mr-3">
              <span class="text-white font-bold text-lg">R</span>
            </div>
            <div>
              <h1 class="text-2xl font-bold text-gray-900">Ruxzom</h1>
              <p class="text-sm text-gray-600">Professional Web Development</p>
            </div>
          </div>
          <div class="text-sm text-gray-600">
            <p>123 Tech Street</p>
            <p>San Francisco, CA 94105</p>
            <p>Phone: +1 (555) 123-4567</p>
            <p>Email: hello@ruxzom.com</p>
          </div>
        </div>
        <div class="text-right">
          <h2 class="text-3xl font-bold text-gray-900 mb-2">INVOICE</h2>
          <div class="text-sm text-gray-600">
            <p><strong>Invoice #:</strong> {{ invoiceNumber }}</p>
            <p><strong>Date:</strong> {{ formatDate(invoiceDate) }}</p>
            <p><strong>Due Date:</strong> {{ formatDate(dueDate) }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Bill To Section -->
    <div class="px-8 py-6 border-b border-gray-200">
      <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div>
          <h3 class="text-lg font-semibold text-gray-900 mb-3">Bill To:</h3>
          <div class="text-sm text-gray-700">
            <p class="font-medium">{{ order.client_name || 'Client Name' }}</p>
            <p>{{ order.client_email || 'client@email.com' }}</p>
            <p v-if="order.client_company">{{ order.client_company }}</p>
            <p v-if="order.billing_address">{{ order.billing_address.address }}</p>
            <p v-if="order.billing_address">
              {{ order.billing_address.city }}, {{ order.billing_address.zip }}
            </p>
          </div>
        </div>
        <div>
          <h3 class="text-lg font-semibold text-gray-900 mb-3">Project Details:</h3>
          <div class="text-sm text-gray-700">
            <p><strong>Order #:</strong> {{ order.order_number }}</p>
            <p><strong>Service:</strong> {{ order.service_name }}</p>
            <p><strong>Package:</strong> {{ order.package_name || 'Custom Package' }}</p>
            <p><strong>Status:</strong> {{ formatStatus(order.status) }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Invoice Items -->
    <div class="px-8 py-6">
      <table class="w-full">
        <thead>
          <tr class="border-b border-gray-200">
            <th class="text-left py-3 text-sm font-semibold text-gray-900">Description</th>
            <th class="text-center py-3 text-sm font-semibold text-gray-900">Qty</th>
            <th class="text-right py-3 text-sm font-semibold text-gray-900">Unit Price</th>
            <th class="text-right py-3 text-sm font-semibold text-gray-900">Total</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in invoiceItems" :key="item.id" class="border-b border-gray-100">
            <td class="py-4 text-sm text-gray-700">
              <div>
                <p class="font-medium">{{ item.name }}</p>
                <p v-if="item.description" class="text-gray-500 text-xs mt-1">{{ item.description }}</p>
              </div>
            </td>
            <td class="py-4 text-center text-sm text-gray-700">{{ item.quantity }}</td>
            <td class="py-4 text-right text-sm text-gray-700">${{ item.unit_price.toLocaleString() }}</td>
            <td class="py-4 text-right text-sm text-gray-700 font-medium">
              ${{ (item.quantity * item.unit_price).toLocaleString() }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Invoice Totals -->
    <div class="px-8 py-6 border-t border-gray-200">
      <div class="flex justify-end">
        <div class="w-64">
          <div class="flex justify-between py-2 text-sm">
            <span class="text-gray-600">Subtotal:</span>
            <span class="text-gray-900">${{ subtotal.toLocaleString() }}</span>
          </div>
          <div v-if="discount > 0" class="flex justify-between py-2 text-sm">
            <span class="text-gray-600">Discount:</span>
            <span class="text-gray-900">-${{ discount.toLocaleString() }}</span>
          </div>
          <div class="flex justify-between py-2 text-sm">
            <span class="text-gray-600">Tax ({{ taxRate }}%):</span>
            <span class="text-gray-900">${{ tax.toLocaleString() }}</span>
          </div>
          <div class="flex justify-between py-3 text-lg font-semibold border-t border-gray-200">
            <span class="text-gray-900">Total:</span>
            <span class="text-gray-900">${{ total.toLocaleString() }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Payment Information -->
    <div class="px-8 py-6 border-t border-gray-200 bg-gray-50">
      <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div>
          <h3 class="text-lg font-semibold text-gray-900 mb-3">Payment Information</h3>
          <div class="text-sm text-gray-700">
            <p v-if="payment"><strong>Payment Status:</strong> {{ formatStatus(payment.status) }}</p>
            <p v-if="payment"><strong>Payment Method:</strong> {{ payment.payment_gateway }}</p>
            <p v-if="payment"><strong>Transaction ID:</strong> {{ payment.transaction_id }}</p>
            <p v-if="payment"><strong>Payment Date:</strong> {{ formatDate(payment.created_at) }}</p>
          </div>
        </div>
        <div>
          <h3 class="text-lg font-semibold text-gray-900 mb-3">Terms & Conditions</h3>
          <div class="text-xs text-gray-600">
            <p class="mb-2">Payment is due within 30 days of invoice date.</p>
            <p class="mb-2">Late payments may incur additional charges.</p>
            <p>For questions about this invoice, please contact us at hello@ruxzom.com</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Footer -->
    <div class="px-8 py-4 border-t border-gray-200 text-center">
      <p class="text-xs text-gray-500">
        Thank you for choosing Ruxzom for your web development needs!
      </p>
    </div>

    <!-- Print/Download Actions -->
    <div v-if="showActions" class="px-8 py-4 border-t border-gray-200 bg-gray-50 print:hidden">
      <div class="flex justify-center space-x-4">
        <button @click="printInvoice" class="btn btn-outline">
          <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"></path>
          </svg>
          Print Invoice
        </button>
        <button @click="downloadPDF" class="btn btn-primary">
          <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
          </svg>
          Download PDF
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'

const props = defineProps({
  order: {
    type: Object,
    required: true
  },
  payment: {
    type: Object,
    default: null
  },
  showActions: {
    type: Boolean,
    default: true
  }
})

const emit = defineEmits(['download', 'print'])

// Invoice data
const invoiceDate = ref(new Date())
const dueDate = ref(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)) // 30 days from now
const taxRate = ref(8.25)
const discount = ref(0)

// Computed properties
const invoiceNumber = computed(() => {
  return `INV-${props.order.order_number || '000001'}`
})

const invoiceItems = computed(() => {
  return [
    {
      id: 1,
      name: props.order.service_name || 'Web Development Service',
      description: props.order.package_name || 'Custom development package',
      quantity: 1,
      unit_price: props.order.total_amount || 0
    }
  ]
})

const subtotal = computed(() => {
  return invoiceItems.value.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0)
})

const tax = computed(() => {
  return Math.round((subtotal.value - discount.value) * (taxRate.value / 100) * 100) / 100
})

const total = computed(() => {
  return subtotal.value - discount.value + tax.value
})

// Methods
const formatDate = (date) => {
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })
}

const formatStatus = (status) => {
  return status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())
}

const printInvoice = () => {
  window.print()
  emit('print')
}

const downloadPDF = () => {
  // This would typically use a library like jsPDF or html2pdf
  // For now, we'll just trigger the browser's print dialog
  window.print()
  emit('download')
}
</script>

<style scoped>
@media print {
  .print\:hidden {
    display: none !important;
  }
  
  #invoice {
    box-shadow: none;
    max-width: none;
    margin: 0;
  }
}
</style>
